/**
 * useKeyboardShortcuts Hook
 * 
 * Provides keyboard navigation shortcuts for live quiz mode.
 * Ignores key events when typing in input fields.
 */

import { useEffect, useCallback } from 'react';

/**
 * @typedef {Object} ShortcutHandlers
 * @property {Function} [onPrevious] - Handler for left arrow
 * @property {Function} [onNext] - Handler for right arrow
 * @property {Function} [onFirst] - Handler for up arrow
 * @property {Function} [onLast] - Handler for down arrow
 * @property {Function} [onToggleResults] - Handler for spacebar
 * @property {Function} [onRevealAnswer] - Handler for Ctrl+R
 * @property {Function} [onLockAnswers] - Handler for Ctrl+L
 */

/**
 * Hook for keyboard shortcuts in live quiz mode
 * 
 * @param {ShortcutHandlers} handlers - Object with handler functions
 * @param {boolean} [enabled=true] - Whether shortcuts are enabled
 */
export function useKeyboardShortcuts(handlers, enabled = true) {
  const {
    onPrevious,
    onNext,
    onFirst,
    onLast,
    onToggleResults,
    onRevealAnswer,
    onLockAnswers
  } = handlers;

  const handleKeyDown = useCallback((e) => {
    // Ignore if typing in input field
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    switch (e.key) {
      case 'ArrowLeft':
        if (onPrevious) {
          e.preventDefault();
          onPrevious();
        }
        break;
      case 'ArrowRight':
        if (onNext) {
          e.preventDefault();
          onNext();
        }
        break;
      case 'ArrowUp':
        if (onFirst) {
          e.preventDefault();
          onFirst();
        }
        break;
      case 'ArrowDown':
        if (onLast) {
          e.preventDefault();
          onLast();
        }
        break;
      case ' ':
        if (onToggleResults) {
          e.preventDefault();
          onToggleResults();
        }
        break;
      case 'r':
        if ((e.ctrlKey || e.metaKey) && onRevealAnswer) {
          e.preventDefault();
          onRevealAnswer();
        }
        break;
      case 'l':
        if ((e.ctrlKey || e.metaKey) && onLockAnswers) {
          e.preventDefault();
          onLockAnswers();
        }
        break;
      default:
        break;
    }
  }, [onPrevious, onNext, onFirst, onLast, onToggleResults, onRevealAnswer, onLockAnswers]);

  useEffect(() => {
    if (!enabled) return;
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
}

/**
 * Returns keyboard shortcuts info for display
 */
export const getShortcutsInfo = () => [
  { key: '←', description: 'Vorherige Frage' },
  { key: '→', description: 'Nächste Frage' },
  { key: '↑', description: 'Erste Frage' },
  { key: '↓', description: 'Letzte Frage' },
  { key: 'Space', description: 'Ergebnisse anzeigen' },
  { key: 'Ctrl+R', description: 'Antwort aufdecken' },
  { key: 'Ctrl+L', description: 'Antworten sperren' }
];

export default useKeyboardShortcuts;
